import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
  Popover,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReplyIcon from "@mui/icons-material/Reply";
import CloseIcon from "@mui/icons-material/Close";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import colors from "../../constants/colors";
import { emojis } from "../../constants/emojis";

const WhatsAppChatScreen = ({
  question,
  user,
  replies,
  loadingReplies,
  onSendReply,
  currentUser,
  onBack,
  onOpenEdit,
  onOpenDelete,
}) => {
  const [message, setMessage] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [replies, replyingTo]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setSending(true);
    try {
      await onSendReply(question._id, message, replyingTo);
      setMessage("");
      setReplyingTo(null);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emoji) => {
    setMessage(prev => prev + emoji);
    setEmojiAnchor(null);
  };

  const flattenReplies = (repliesArray, parent = null, level = 0) => {
    let result = [];
    for (const reply of repliesArray) {
      result.push({ 
        ...reply, 
        parentReply: parent,
        level: level 
      });
      if (reply.children && reply.children.length > 0) {
        result = result.concat(flattenReplies(reply.children, reply, level + 1));
      }
    }
    return result;
  };

  // Group messages by date
  const groupMessagesByDate = () => {
    const allMessages = [
      {
        _id: question._id,
        type: 'question',
        contentText: question.contentText,
        createdAt: question.createdAt,
        user: user,
        isQuestion: true
      },
      ...flattenReplies(replies).map(reply => ({
        ...reply,
        type: 'reply',
        user: reply.repliedBy,
        isQuestion: false
      }))
    ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    const groups = {};
    allMessages.forEach(msg => {
      const date = new Date(msg.createdAt).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(msg);
    });

    return groups;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  const isAdmin = (user) => {
    return user?.role === "admin" || 
           user?.isAdmin || 
           (user?.name && user.name.toLowerCase().includes("admin")) ||
           (user?.email && user.email.toLowerCase().includes("admin")) ||
           user?._id === currentUser?._id;
  };

  const isCurrentUser = (messageUser) => {
    return messageUser?._id === currentUser?._id;
  };

  const messageGroups = groupMessagesByDate();

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100vh',
      background: colors.whatsappChatBg,
    }}>
      {/* Fixed Header Section */}
      <Box sx={{ 
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <Paper 
          elevation={1}
          sx={{ 
            p: 1, 
            background: colors.whatsappGreen,
            color: 'white',
            borderRadius: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <IconButton onClick={onBack} sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              src={user?.profilePicture}
              alt={user?.name}
              sx={{ bgcolor: 'rgba(255,255,255,0.3)' }}
            >
              {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {question.contentText}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.8rem' }}>
                Asked by: {user?.name || "User"}
              </Typography>
            </Box>

            {/* Admin actions inside opened chat */}
            {isAdmin(currentUser) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Edit Question">
                  <IconButton
                    onClick={() => typeof (onOpenEdit) === 'function' && onOpenEdit(question)}
                    sx={{ color: 'white' }}
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete Question">
                  <IconButton
                    onClick={() => typeof (onOpenDelete) === 'function' && onOpenDelete(question)}
                    sx={{ color: 'white' }}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Scrollable Messages Section - Only this part scrolls */}
      <Box 
        ref={chatContainerRef}
        sx={{ 
          flex: 1,
          overflow: 'auto',
          p: 4,
          mt:5,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\"100\" height=\"100\" viewBox=\"0 0 100 100\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath d=\"M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\" fill=\"%23f0f0f0\" fill-opacity=\"0.4\" fill-rule=\"evenodd\"/%3E%3C/svg%3E")',
          // Hide scrollbar but keep scrolling
          '&::-webkit-scrollbar': {
            width: '0px',
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
        }}
      >
        {loadingReplies ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          Object.entries(messageGroups).map(([date, messages]) => (
            <Box key={date}>
              {/* Date Separator */}
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Paper
                  sx={{
                    px: 2,
                    py: 0.5,
                    background: 'rgba(0,0,0,0.1)',
                    borderRadius: 20
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(date)}
                  </Typography>
                </Paper>
              </Box>

              {/* Messages */}
              {messages.map((msg) => {
                const messageUser = msg.user;
                const admin = isAdmin(messageUser);
                const isUser = isCurrentUser(messageUser);
                
                return (
                  <Box
                    key={msg._id}
                    sx={{
                      display: 'flex',
                      justifyContent: isUser ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '70%',
                        minWidth: '120px'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: isUser ? 'flex-end' : 'flex-start'
                        }}
                      >
                        {/* Reply Context */}
                        {msg.parentReply && (
                          <Paper
                            sx={{
                              p: 1,
                              mb: 0.5,
                              background: isUser ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.05)',
                              borderLeft: `3px solid ${isUser ? colors.whatsappGreen : '#999'}`,
                              borderRadius: '8px 0 0 8px',
                              maxWidth: '90%',
                              alignSelf: isUser ? 'flex-end' : 'flex-start'
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                              Replying to {isAdmin(msg.parentReply.repliedBy) ? "Admin" : msg.parentReply.repliedBy?.name}:
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                mt: 0.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                fontSize: '0.7rem'
                              }}
                            >
                              {msg.parentReply.replyText}
                            </Typography>
                          </Paper>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                          {/* Reply button for others' messages */}
                          {!isUser && (
                            <IconButton
                              size="small"
                              onClick={() => setReplyingTo(msg)}
                              sx={{
                                mt: 0.5,
                                color: 'text.secondary',
                                '&:hover': {
                                  color: colors.whatsappGreen,
                                }
                              }}
                            >
                              <ReplyIcon fontSize="small" />
                            </IconButton>
                          )}

                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                              background: msg.isQuestion ? colors.whatsappGreen : 
                                        isUser ? colors.whatsappGreen : colors.white,
                              color: (isUser || msg.isQuestion) ? 'white' : 'text.primary',
                              position: 'relative',
                              maxWidth: '100%',
                            }}
                          >
                            {/* Message header with name and time */}
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              mb: 0.5,
                              gap: 1
                            }}>
                              {/* Left side - Name and badges */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                                {/* Admin badge */}
                                {admin && !msg.isQuestion && (
                                  <>
                                    <AdminPanelSettingsIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', fontWeight: 600 }}>
                                      Admin
                                    </Typography>
                                  </>
                                )}

                                {/* Question indicator */}
                                {msg.isQuestion && (
                                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, fontSize: '0.7rem' }}>
                                    QUESTION
                                  </Typography>
                                )}

                                {/* User name for non-current user messages */}
                                {!isUser && !admin && !msg.isQuestion && (
                                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    {messageUser?.name || "User"}
                                  </Typography>
                                )}
                              </Box>

                              {/* Right side - Time */}
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  opacity: 0.7,
                                  fontSize: '0.65rem',
                                  flexShrink: 0
                                }}
                              >
                                {formatTime(msg.createdAt)}
                              </Typography>
                            </Box>

                            {/* Message content */}
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                              {msg.contentText || msg.replyText}
                            </Typography>
                          </Paper>

                          {/* Reply button for current user messages */}
                          {isUser && (
                            <IconButton
                              size="small"
                              onClick={() => setReplyingTo(msg)}
                              sx={{
                                mt: 0.5,
                                color: 'text.secondary',
                                '&:hover': {
                                  color: colors.whatsappGreen,
                                }
                              }}
                            >
                              <ReplyIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Fixed Input Section */}
      <Box sx={{ 
        flexShrink: 0,
        position: 'sticky',
        bottom: 0,
        background: colors.lightGray,
        zIndex: 10
      }}>
        {/* Reply Context */}
        {replyingTo && (
          <Paper
            sx={{
              p: 1,
              mx: 2,
              mt: 1,
              background: 'rgba(0,0,0,0.05)',
              borderLeft: `3px solid ${colors.whatsappGreen}`,
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  Replying to {isAdmin(replyingTo.user) ? "Admin" : replyingTo.user?.name}:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    fontSize: '0.8rem'
                  }}
                >
                  {replyingTo.contentText || replyingTo.replyText}
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={() => setReplyingTo(null)}
                sx={{ mt: -0.5, mr: -0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        )}

        {/* Input Area */}
        <Box sx={{ 
          p: 1.5, 
          borderTop: `1px solid ${colors.borderGray}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            {/* Emoji Picker Button */}
            <IconButton
              onClick={(e) => setEmojiAnchor(e.currentTarget)}
              sx={{
                color: colors.whatsappGreen,
                '&:hover': {
                  background: 'rgba(18, 140, 126, 0.1)',
                }
              }}
            >
              <EmojiEmotionsIcon />
            </IconButton>

            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={replyingTo ? "Type your reply..." : "Type a message..."}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              variant="outlined"
              size="small"
              sx={{
                background: 'white',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.9rem'
                }
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!message.trim() || sending}
              sx={{
                background: colors.whatsappGreen,
                color: 'white',
                '&:hover': {
                  background: colors.whatsappGreenDark,
                },
                '&:disabled': {
                  background: colors.midGray,
                }
              }}
            >
              {sending ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            width: 350,
            height: 250,
            p: 1.5,
            overflow: 'auto',
            borderRadius: 3,
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            background: 'white',
            // Fix blur by ensuring crisp rendering
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            // Hide scrollbar but keep scrolling
            '&::-webkit-scrollbar': {
              width: '0px',
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'transparent',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }
        }}
      >
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 0.5,
        }}>
          {emojis.map((emoji, index) => (
            <Box
              key={index}
              onClick={() => handleEmojiClick(emoji)}
              sx={{
                fontSize: '1.5rem',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': {
                  background: 'rgba(18, 140, 126, 0.1)',
                },
                // Ensure crisp rendering
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden',
              }}
            >
              <span style={{ 
                display: 'block', 
                lineHeight: 1,
                fontSize: '1.5rem',
                fontFamily: 'system-ui, "Segoe UI Emoji", "Apple Color Emoji", sans-serif',
                // Force crisp rendering
                transform: 'translateZ(0)',
                textRendering: 'optimizeLegibility',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
              }}>
                {emoji}
              </span>
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default WhatsAppChatScreen;